<div class="bots-page">

  <!-- ── Header ─────────────────────────────────────────── -->
  <div class="page-header">
    <div class="header-left">
      <div class="title-row">
        <h1>Devices</h1>
        <span class="count-pill">{{ totalDevices }} devices</span>
      </div>
      <div class="subtitle-row">
        <span class="stat-online">
          <span class="dot"></span>
          <strong>{{ onlineCount }}</strong>&nbsp;online
        </span>
        <span class="stat-offline">
          <span class="dot"></span>
          <strong>{{ offlineCount }}</strong>&nbsp;offline
        </span>
      </div>
    </div>
    <button class="btn-create" (click)="openCreateBotDialog()">
      <mat-icon>add</mat-icon>
      Create device
    </button>
  </div>

  <!-- ── Filter bar ─────────────────────────────────────── -->
  <div class="filter-bar">
    <div class="search-wrap">
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4-4"/></svg>
      <input class="search-input" placeholder="Search by name or ID…" />
      <span class="kbd">⌘K</span>
    </div>

    <div class="status-pills">
      <button class="pill" [class.active]="statusFilter === 'all'"         (click)="onStatusFilterChange('all')">All</button>
      <button class="pill" [class.active]="statusFilter === 'online'"      (click)="onStatusFilterChange('online')">Online</button>
      <button class="pill" [class.active]="statusFilter === 'offline'"     (click)="onStatusFilterChange('offline')">Offline</button>
      <button class="pill" [class.active]="statusFilter === 'deactivated'" (click)="onStatusFilterChange('deactivated')">Deactivated</button>
    </div>

    <div class="spacer"></div>

    <div class="view-toggle">
      <button [class.active]="viewMode() === 'grid'" (click)="setViewMode('grid')">
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 5h7v6H3zM14 5h7v6h-7zM3 14h7v6H3zM14 14h7v6h-7z"/></svg>
        Grid
      </button>
      <button [class.active]="viewMode() === 'rows'" (click)="setViewMode('rows')">
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 6h18M3 12h18M3 18h18"/></svg>
        Rows
      </button>
    </div>
  </div>

  <!-- ── Loading ────────────────────────────────────────── -->
  @if (isLoading()) {
    <div class="skeleton-grid">
      <div class="skeleton-tile" *ngFor="let i of [0,1,2,3,4,5]">
        <div class="skel-photo"></div>
        <div class="skel-body">
          <div class="skel-line" style="width:55%"></div>
          <div class="skel-line" style="width:35%"></div>
          <div class="skel-line" style="width:75%"></div>
        </div>
      </div>
    </div>
  }

  <!-- ── Error ──────────────────────────────────────────── -->
  @if (error() && !isLoading()) {
    <div class="error-banner">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
      {{ error() }}
      <button class="retry-btn" (click)="retryLoad()">Retry</button>
    </div>
  }

  <!-- ── Grid view ──────────────────────────────────────── -->
  @if (!isLoading() && viewMode() === 'grid') {
    <div class="devices-grid">

      <div class="device-tile" *ngFor="let device of displayedDevices()">
        <div class="tile-photo"
             [style.backgroundImage]="device.mainPhotoUrl ? 'url(' + device.mainPhotoUrl + ')' : null"
             [class.no-photo]="!device.mainPhotoUrl">
          <div class="photo-overlay"></div>
          <div class="tile-status-pill" [class.is-online]="device.status === 'online'">
            <span class="status-dot"></span>
            {{ device.status === 'online' ? 'Online' : 'Offline' }}
          </div>
          <div class="tile-name-block">
            <div class="tile-name">{{ device.name }}</div>
          </div>
        </div>

        <div class="tile-body">
          <div class="tile-stats">
            <div class="stat">
              <div class="stat-label">Cameras</div>
              <div class="stat-value">{{ device.cockpits.length || '—' }}</div>
            </div>
            <div class="stat">
              <div class="stat-label">Status</div>
              <div class="stat-value" [class.value-online]="device.status === 'online'">
                {{ device.status | titlecase }}
              </div>
            </div>
          </div>

          <div class="device-id-chip">
            <span class="id-label">id&nbsp;</span>{{ device.id }}
          </div>

          <div class="tile-actions">
            <button class="icon-btn" title="Edit" (click)="onEdit(device)">
              <mat-icon>edit</mat-icon>
            </button>
            <button class="icon-btn" title="Delete" (click)="onDelete(device)">
              <mat-icon>delete_outline</mat-icon>
            </button>
            <div class="spacer"></div>
            <button class="btn-connect"
                    [class.btn-connect--disabled]="device.status !== 'online'"
                    [disabled]="device.status !== 'online'"
                    (click)="onConnect(device)">
              <mat-icon>arrow_forward</mat-icon>
              Connect
            </button>
          </div>
        </div>
      </div>

      <!-- Add a device tile -->
      <div class="add-tile" (click)="openCreateBotDialog()">
        <div class="add-tile-icon">
          <mat-icon>add</mat-icon>
        </div>
        <div class="add-tile-label">Add a device</div>
        <div class="add-tile-sub">Pi · Jetson · custom Linux</div>
      </div>

    </div>
  }

  <!-- ── Rows view ──────────────────────────────────────── -->
  @if (!isLoading() && viewMode() === 'rows' && displayedDevices().length > 0) {
    <div class="devices-rows">
      <div class="rows-header">
        <div class="col-device">Device</div>
        <div class="col-status">Status</div>
        <div class="col-cameras">Cameras</div>
        <div class="col-id">ID</div>
        <div class="col-actions"></div>
      </div>

      <div class="device-row"
           *ngFor="let device of displayedDevices(); let last = last"
           [class.last]="last">
        <div class="col-device">
          <div class="row-thumb"
               [style.backgroundImage]="device.mainPhotoUrl ? 'url(' + device.mainPhotoUrl + ')' : null"
               [class.no-photo]="!device.mainPhotoUrl">
            <div class="status-dot-corner" [class.is-online]="device.status === 'online'"></div>
          </div>
          <div class="row-name">{{ device.name }}</div>
        </div>

        <div class="col-status">
          <div class="row-status-pill" [class.is-online]="device.status === 'online'">
            <span class="status-dot"></span>
            {{ device.status === 'online' ? 'Online' : 'Offline' }}
          </div>
        </div>

        <div class="col-cameras">
          {{ device.cockpits.length === 0 ? '—' : device.cockpits.length + ' attached' }}
        </div>

        <div class="col-id">
          {{ device.id.slice(0,8) }}…{{ device.id.slice(-4) }}
        </div>

        <div class="col-actions">
          <button class="icon-btn-sm" title="Edit" (click)="onEdit(device)">
            <mat-icon>edit</mat-icon>
          </button>
          <button class="icon-btn-sm" title="Delete" (click)="onDelete(device)">
            <mat-icon>delete_outline</mat-icon>
          </button>
          <button class="btn-connect-sm"
                  (click)="onConnect(device)">
            Connect1
          </button>
        </div>
      </div>
    </div>
  }

  <!-- ── Empty state ────────────────────────────────────── -->
  @if (!isLoading() && !error() && displayedDevices().length === 0) {
    <div class="empty-state">
      @if (totalDevices === 0) {
        <div class="add-tile add-tile--lg" (click)="openCreateBotDialog()">
          <div class="add-tile-icon">
            <mat-icon>add</mat-icon>
          </div>
          <div class="add-tile-label">Add your first device</div>
          <div class="add-tile-sub">Flash your Pi, Jetson, or custom Linux SBC and connect it here.</div>
        </div>
      } @else {
        <div class="empty-filtered">
          <p>No devices match the current filter.</p>
          <button class="btn-outline" (click)="clearFilter()">Clear filter</button>
        </div>
      }
    </div>
  }

  <!-- ── Footer ─────────────────────────────────────────── -->
  @if (totalDevices > 0 && !isLoading()) {
    <div class="page-footer">
      <span class="footer-showing">Showing {{ startItem }}–{{ endItem }} of {{ totalDevices }}</span>
      <div class="spacer"></div>
      <label class="per-page">
        Per page
        <select (change)="onPageSizeChange($event)">
          <option *ngFor="let s of pageSizeOptions" [value]="s" [selected]="s === pageSize">{{ s }}</option>
        </select>
      </label>
      <div class="pagination-btns">
        <button class="page-btn" [disabled]="pageIndex === 0"           (click)="prevPage()">‹</button>
        <button class="page-btn" [disabled]="endItem >= totalDevices"   (click)="nextPage()">›</button>
      </div>
    </div>
  }

</div>
